import React, { useMemo } from "react";
import { Play, CalendarDays } from "lucide-react";
import { Button } from "@/components/ui/button";


function startOfDay(d) {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}

function addDays(d, n) {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
}

function formatMonthYear(d) {
  return d.toLocaleString(undefined, { month: "long", year: "numeric" });
}

function formatDayLabel(d) {
  return d.toLocaleString(undefined, { weekday: "short" });
}

function formatMmdd(d) {
  return d.toLocaleString(undefined, { month: "numeric", day: "numeric" });
}

function formatTime(d) {
  return d.toLocaleString(undefined, { hour: "numeric", minute: "2-digit" });
}

function getDispositionStyle(status) {
  const s = (status || "").toLowerCase();
  switch (s) {
    case "completed":
      return "bg-purple-500/20 text-purple-200 ring-1 ring-purple-400/30";
    case "scheduled":
      return "bg-blue-500/20 text-blue-200 ring-1 ring-blue-400/30";
    case "rebooked":
      return "bg-indigo-500/20 text-indigo-200 ring-1 ring-indigo-400/30";
    case "ghosted":
      return "bg-yellow-500/20 text-yellow-200 ring-1 ring-yellow-400/30";
    case "canceled":
    case "cancelled":
      return "bg-rose-500/20 text-rose-200 ring-1 ring-rose-400/30";
    case "sold":
      return "bg-green-500/20 text-green-200 ring-1 ring-green-400/30";
    default:
      return "bg-white/10 text-slate-100 ring-1 ring-white/15";
  }
}

export default function MiniCalendar({
  view,
  events,
  date,
  onEventClick,
  onStartRunClick,
  agendaLimit = 10,
}) {
  const baseDate = useMemo(() => (date ? new Date(date) : new Date()), [date]);

  if (view === "agenda") {
    const today = startOfDay(baseDate);
    const end = addDays(today, 3);

    const rowsAll = (events || [])
      .map((e) => ({ ...e, start: new Date(e.start_at), end: e.end_at ? new Date(e.end_at) : null }))
      .filter((e) => e.start >= today && e.start < end)
      .sort((a, b) => +a.start - +b.start);

    const rows = rowsAll.slice(0, agendaLimit);

    const groups = rows.reduce((acc, e) => {
      const k = startOfDay(e.start).toISOString();
      acc[k] = acc[k] || { date: startOfDay(e.start), items: [] };
      acc[k].items.push(e);
      return acc;
    }, {});

    const groupList = Object.values(groups).sort((a, b) => +a.date - +b.date);

    return (
      <div className="rounded-2xl bg-white/5 ring-1 ring-white/10">
              {rowsAll.length > agendaLimit ? (
                <div className="mt-3 rounded-xl bg-white/5 px-4 py-2 text-xs text-slate-200/60 ring-1 ring-white/10">
                  Showing first {agendaLimit} appointments for this range.
                </div>
              ) : null}

        {rowsAll.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <CalendarDays className="h-12 w-12 text-indigo-400 mb-3" />
            <div className="text-sm text-slate-200/70">No appointments scheduled</div>
          </div>
        ) : (
          <div className="px-4 py-3">
            {groupList.map((g) => (
              <div key={g.date.toISOString()} className="mb-4 last:mb-0">
                <div className="text-xs font-semibold text-slate-200/70">
                  {g.date.toLocaleString(undefined, { day: "numeric", month: "short" })}, {g.date.toLocaleString(undefined, { weekday: "short" })}
                </div>
                <div className="mt-3 space-y-3">
                  {g.items.map((e) => (
                    <div
                      key={e.id}
                      className="w-full rounded-xl bg-white/5 px-4 py-3 text-left ring-1 ring-white/10 hover:bg-white/10"
                      role="button"
                      tabIndex={0}
                      onClick={() => onEventClick?.(e)}
                      onKeyDown={(ev) => {
                        if (ev.key === "Enter" || ev.key === " ") onEventClick?.(e);
                      }}
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-1 self-stretch rounded-full bg-indigo-500/60" />
                        <div className="min-w-0 flex-1">
                          <div className="truncate text-sm font-semibold text-slate-100">{e.lead_snapshot?.full_name || e.title}</div>
                          <div className="mt-1 text-sm text-slate-200/70">
                            {formatTime(e.start)}{e.end ? ` - ${formatTime(e.end)}` : ""}
                          </div>
                        </div>

                        {e?.summary_payload?.appointment_outcome ? (
                          <span
                            className={`ml-3 inline-flex shrink-0 items-center rounded-full px-3 py-1 text-xs font-semibold ${getDispositionStyle(
                              e.summary_payload.appointment_outcome
                            )}`}
                            data-testid={`agenda-disposition-${e.id}`}
                          >
                            {e.summary_payload.appointment_outcome}
                          </span>
                        ) : onStartRunClick ? (
                          <Button
                            size="sm"
                            onClick={(ev) => {
                              ev.stopPropagation();
                              onStartRunClick(e);
                            }}
                            data-testid={`agenda-start-run-${e.id}`}
                            className="ml-3 shrink-0"
                          >
                            <span className="inline-flex items-center gap-1">
                              <Play className="h-3.5 w-3.5" />
                              Start Run
                            </span>
                          </Button>
                        ) : null}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  if (view === "month") {
    const base = baseDate;
    const year = base.getFullYear();
    const month = base.getMonth();
    const first = new Date(year, month, 1);
    const start = addDays(first, -((first.getDay() + 6) % 7)); // monday-start

    const days = Array.from({ length: 42 }, (_, i) => addDays(start, i));
    const byDay = new Map();
    (events || []).forEach((e) => {
      const d = new Date(e.start_at);
      const key = startOfDay(d).toISOString();
      byDay.set(key, (byDay.get(key) || []).concat([e]));
    });

    return (
      <div className="rounded-2xl bg-white/5 ring-1 ring-white/10 overflow-hidden">
        <div className="grid grid-cols-7 border-b border-white/10 bg-white/5">
          {Array.from({ length: 7 }, (_, i) => {
            const d = addDays(start, i);
            return (
              <div key={i} className="px-3 py-2 text-xs font-semibold text-slate-200/70">
                {formatDayLabel(d)}
              </div>
            );
          })}
        </div>
        <div className="grid grid-cols-7">
          {days.map((d) => {
            const inMonth = d.getMonth() === month;
            const dayKey = startOfDay(d).toISOString();
            const dayEvents = byDay.get(dayKey) || [];
            return (
              <div key={dayKey} className={"min-h-[84px] border-b border-white/10 border-r border-white/10 p-2 " + (inMonth ? "" : "opacity-50")}>
                <div className="text-xs text-slate-200/70">{d.getDate()}</div>
                {dayEvents.slice(0, 1).map((e) => (
                  <button
                    key={e.id}
                    type="button"
                    className="mt-2 block w-full truncate rounded-lg bg-indigo-500/20 px-2 py-1 text-xs text-indigo-100 ring-1 ring-indigo-400/20"
                    onClick={() => onEventClick?.(e)}
                  >
                    {e.title}
                  </button>
                ))}
                {dayEvents.length > 1 ? <div className="mt-1 text-xs text-slate-200/60">+{dayEvents.length - 1} more</div> : null}
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  if (view === "week") {
    const today = startOfDay(baseDate);
    const monday = addDays(today, -((today.getDay() + 6) % 7));
    const days = Array.from({ length: 7 }, (_, i) => addDays(monday, i));
    const hours = Array.from({ length: 12 }, (_, i) => i + 8);

    return (
      <div className="rounded-2xl bg-white/5 ring-1 ring-white/10 overflow-hidden">
        <div className="grid grid-cols-[80px_repeat(7,1fr)] border-b border-white/10 bg-white/5">
          <div />
          {days.map((d) => (
            <div key={d.toISOString()} className="px-3 py-2 text-xs font-semibold text-slate-200/70">
              {formatDayLabel(d)} {formatMmdd(d)}
            </div>
          ))}
        </div>
        <div className="max-h-[420px] overflow-auto">
          {hours.map((h) => (
            <div key={h} className="grid grid-cols-[80px_repeat(7,1fr)] border-b border-white/10">
              <div className="px-3 py-2 text-xs text-slate-200/60">{h <= 12 ? `${h}:00` : `${h - 12}:00`} </div>
              {days.map((d) => (
                <div key={d.toISOString() + h} className="h-12 border-r border-white/10" />
              ))}
            </div>
          ))}
        </div>
      </div>
    );
  }

  // day
  const hours = Array.from({ length: 12 }, (_, i) => i + 8);
  const dayLabel = baseDate.toLocaleString(undefined, { weekday: "short", month: "short", day: "numeric", year: "numeric" });

  return (
    <div className="rounded-2xl bg-white/5 ring-1 ring-white/10 overflow-hidden">
      <div className="grid grid-cols-[80px_1fr] border-b border-white/10 bg-white/5">
        <div />
        <div className="px-3 py-2 text-xs font-semibold text-slate-200/70">{dayLabel}</div>
      </div>
      <div className="max-h-[420px] overflow-auto">
        {hours.map((h) => (
          <div key={h} className="grid grid-cols-[80px_1fr] border-b border-white/10">
            <div className="px-3 py-2 text-xs text-slate-200/60">{h <= 12 ? `${h}:00` : `${h - 12}:00`}</div>
            <div className="h-12" />
          </div>
        ))}
      </div>
    </div>
  );
}

export function CalendarHeader({ title, className = "" }) {
  return <div className={("text-base font-semibold text-slate-100 " + className).trim()}>{title}</div>;
}
